import { z } from 'zod';
import { operatorApiFetch } from './apiService';
import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

// ── Shared inner schemas ────────────────────────────────────────────────
// Defined inline; promote to @services/schemas/inventoryHold.ts when a 3rd
// service needs them. .passthrough() per project convention — forward-compat
// when backend adds fields.

const MerchantRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
}).passthrough();

// get_records items use brief shape (controller line 33-44)
const InventoryBriefSchema = z.object({
  id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
}).passthrough();

// show items use detail shape (controller line 76-92) — adds product_id (for
// Show page product link), inventory_type_name, manufacturing_date,
// custom_attributes. product_id is non-nullable because Inventory.belongs_to
// :product is required (app/models/inventory.rb:11); controller emits
// inventory.product.id without &..
const InventoryDetailSchema = InventoryBriefSchema.extend({
  product_id: z.string(),
  inventory_type_name: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.unknown(),
}).passthrough();

const HoldItemBriefSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  inventory: InventoryBriefSchema,
}).passthrough();

const HoldItemDetailSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  inventory: InventoryDetailSchema,
}).passthrough();

// ── Per-endpoint schemas ────────────────────────────────────────────────

// Api::Operator::V1::InventoryHoldsController#get_records (line 24-52)
const OperatorInventoryHoldRowSchema = z.object({
  id: z.string(),
  number: z.string(),
  reason: z.string(),
  note: z.string().nullable(),
  expires_on: z.string().nullable(),
  created_at: z.string(),
  total_quantity: z.number(),
  items: z.array(HoldItemBriefSchema),
  merchant: MerchantRefSchema,
  held_by: z.string(),
}).passthrough();

// Api::Operator::V1::InventoryHoldsController#show (line 56-93)
const OperatorInventoryHoldShowSchema = z.object({
  id: z.string(),
  number: z.string(),
  reason: z.string(),
  note: z.string().nullable(),
  expires_on: z.string().nullable(),
  released_at: z.string().nullable(),
  release_note: z.string().nullable(),
  created_at: z.string(),
  merchant: MerchantRefSchema,
  held_by: z.string(),
  released_by: z.string().nullable(),
  items: z.array(HoldItemDetailSchema),
}).passthrough();

// Api::Operator::V1::InventoryHoldsController#create (line 140) — returns { id, number }
const InventoryHoldCreatedSchema = z.object({
  id: z.string(),
  number: z.string(),
}).passthrough();

// batch_release returns render_success (envelope only, no data field)
const SuccessEnvelopeSchema = z.object({
  success: z.literal(true),
  message: z.string(),
}).passthrough();

export const inventoryHoldService = {
  getRecords: async (params: Record<string, unknown>) => {
    const res = await operatorApiFetch('inventory_holds/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseApiResponse(res, apiList(OperatorInventoryHoldRowSchema));
  },

  get: async (id: string) => {
    const res = await operatorApiFetch(`inventory_holds/${id}`);
    return parseApiResponse(res, apiSuccess(OperatorInventoryHoldShowSchema));
  },

  create: async (params: Record<string, unknown>) => {
    const res = await operatorApiFetch('inventory_holds', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseApiResponse(res, apiSuccess(InventoryHoldCreatedSchema));
  },

  batchRelease: async (ids: string[], releaseNote: string = '') => {
    const res = await operatorApiFetch('inventory_holds/batch_release', {
      method: 'POST',
      body: JSON.stringify({ ids, release_note: releaseNote }),
    });
    return parseApiResponse(res, SuccessEnvelopeSchema);
  },
};

export default inventoryHoldService;

// z.infer types for callers that want to type their state
export type OperatorInventoryHoldRow = z.infer<typeof OperatorInventoryHoldRowSchema>;
export type OperatorInventoryHoldDetail = z.infer<typeof OperatorInventoryHoldShowSchema>;
export type InventoryHoldCreated = z.infer<typeof InventoryHoldCreatedSchema>;
